import axios from "axios";
import { ENDPOINTS } from "./endpoints";
import tokenServiceClient from "./tokenServiceClient";

const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || "",
    headers: { "Content-Type": "application/json" },
});

const SAFETY_MARGIN_MS = 30_000;
const PROACTIVE_REFRESH_MS = 60_000;
const FALLBACK_TTL_MS = 8 * 60 * 1000;

let tokenCache = { promise: null, expiresAt: 0 };
let tokenGeneration = 0;
let refreshTimer = null;

function clearTokenCache() {
    clearTimeout(refreshTimer);
    tokenGeneration++;
    tokenCache = { promise: null, expiresAt: 0 };
}

function getTokenExpiryMs(jwt) {
    try {
        const payload = JSON.parse(atob(jwt.split(".")[1]));
        return payload.exp ? payload.exp * 1000 : 0;
    } catch {
        return 0;
    }
}

function scheduleProactiveRefresh(expiresAt) {
    clearTimeout(refreshTimer);
    const refreshIn = Math.max(expiresAt - Date.now() - PROACTIVE_REFRESH_MS, 0);
    refreshTimer = setTimeout(() => {
        clearTokenCache();
        fetchToken().catch(() => {});
    }, refreshIn);
}

function isTokenValid() {
    if (!tokenCache.promise || !tokenCache.expiresAt) return false;
    return Date.now() < tokenCache.expiresAt - SAFETY_MARGIN_MS;
}

function fetchToken() {
    if (isTokenValid()) {
        return tokenCache.promise;
    }

    // Return the existing in-flight promise so concurrent callers don't each
    // fire a new /token request. expiresAt is only set after resolution, so
    // isTokenValid() returns false during the fetch — guard separately.
    if (tokenCache.promise) {
        return tokenCache.promise;
    }

    clearTimeout(refreshTimer);
    const gen = ++tokenGeneration;
    tokenCache = { promise: null, expiresAt: 0 };

    tokenCache.promise = tokenServiceClient
        .get(ENDPOINTS.token)
        .then((d) => {
            if (gen !== tokenGeneration) return d?.customTokenValue;
            const jwt = d?.customTokenValue;
            const expiresAt = getTokenExpiryMs(jwt) || Date.now() + FALLBACK_TTL_MS;
            tokenCache.expiresAt = expiresAt;
            scheduleProactiveRefresh(expiresAt);
            return jwt;
        })
        .catch((err) => {
            if (gen === tokenGeneration) {
                clearTimeout(refreshTimer);
                tokenCache = { promise: null, expiresAt: 0 };
            }
            return Promise.reject(err);
        });
    return tokenCache.promise;
}

apiClient.interceptors.request.use(async (config) => {
    const jwt = await fetchToken();
    if (jwt) {
        config.headers.Authorization = `Bearer ${jwt}`;
    }
    return config;
});

apiClient.interceptors.response.use(
    (response) => response.data,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;

        if (status === 503) {
            if (navigator.onLine) {
                const controller = new AbortController();
                setTimeout(() => controller.abort(), 3000);
                fetch(import.meta.env.VITE_FAILOVER_UI_URL, { mode: 'no-cors', method: 'HEAD', signal: controller.signal })
                    .then(() => window.location.replace("/nothealthy"))
                    .catch(() => {});
            }
            return new Promise(() => {});
        }

        if (status === 429 && !original._retry429) {
            original._retry429 = true;
            const retryAfter = error.response.headers?.["retry-after"];
            const delayMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 5000;
            await new Promise((r) => setTimeout(r, delayMs));
            return apiClient(original);
        }

        if (status === 401 && !original._retry) {
            original._retry = true;
            clearTokenCache();
            try {
                const jwt = await fetchToken();
                original.headers.Authorization = `Bearer ${jwt}`;
                return apiClient(original);
            } catch (refreshErr) {
                return Promise.reject(refreshErr);
            }
        }
        return Promise.reject(error);
    },
);

document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isTokenValid() && !tokenCache.promise) {
        fetchToken().catch(() => {});
    }
});

export default apiClient;
